import { useEffect, useRef, useState } from 'react'

interface PasswordLockProps {
  onUnlock: (password: string) => Promise<boolean>
  isTimeout?: boolean
}

export default function PasswordLock({ onUnlock, isTimeout = false }: PasswordLockProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Refocus the password input whenever an error appears and the input is enabled.
  // The input is disabled while isVerifying === true, so we wait for it to flip back.
  useEffect(() => {
    if (error && !isVerifying) {
      inputRef.current?.focus()
    }
  }, [error, isVerifying])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) {
      setError('Please enter your password')
      return
    }

    setIsVerifying(true)
    setError('')

    try {
      const isValid = await onUnlock(password)
      if (!isValid) {
        setError('Incorrect password')
        setPassword('')
      }
    } catch {
      setError('Failed to verify password')
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--bg-primary)]">
      <div className="w-full max-w-sm px-6">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <svg
              className="w-12 h-12 text-[var(--accent-teal)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8l-1 16H9L8 4z" />
              <ellipse cx="12" cy="4" rx="4" ry="1.5" />
              <circle cx="10" cy="10" r="0.5" fill="currentColor" />
              <circle cx="14" cy="8" r="0.5" fill="currentColor" />
              <circle cx="11" cy="14" r="0.5" fill="currentColor" />
            </svg>
            <span className="text-2xl font-bold text-[var(--text-primary)]">
              SSDL<span className="text-[var(--accent-teal)]">.app</span>
            </span>
          </div>
        </div>

        {/* Lock Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[var(--bg-card)] flex items-center justify-center">
            <svg
              className="w-8 h-8 text-[var(--accent-teal)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-semibold text-center text-[var(--text-primary)] mb-2">
          {isTimeout ? 'Session Locked' : 'Password Protected'}
        </h1>
        <p className="text-sm text-center text-[var(--text-secondary)] mb-4">
          {isTimeout
            ? 'Your session has been locked due to inactivity'
            : 'Enter your password to view your drink log'}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 text-center text-lg"
              autoFocus
              disabled={isVerifying}
            />
          </div>

          {error && (
            <p className="text-sm text-center text-[var(--bac-danger)] mb-4">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isVerifying}
            className="w-full btn btn-primary py-3 text-lg disabled:opacity-50"
          >
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <span className="spinner" />
                Verifying...
              </span>
            ) : (
              'Unlock'
            )}
          </button>
        </form>

        {/* Recovery hint */}
        <p className="mt-6 text-xs text-center text-[var(--text-secondary)]">
          Forgot your password? If you set up a backup email, visit the{' '}
          <a href="/recover" className="text-[var(--accent-teal)] hover:underline">
            recovery page
          </a>
          . Otherwise, your data cannot be recovered.
        </p>
      </div>
    </div>
  )
}
